import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";

/**
 * Polls /api/notifications/unseen every 20s. Renders a bell icon with a red badge.
 * Clicking navigates to /admin/orders and marks-seen. If the count grows while
 * the admin is on any other page, a toast is shown.
 */
export default function AdminNotificationBell() {
    const [count, setCount] = useState(0);
    const [latest, setLatest] = useState([]);
    const prevRef = useRef(0);
    const nav = useNavigate();

    const poll = async () => {
        try {
            const r = await api.get("/notifications/unseen");
            const n = r.data.new_orders || 0;
            setLatest(r.data.recent || []);
            if (n > prevRef.current && prevRef.current > 0) {
                // A brand-new order arrived while we were watching
                const fresh = (r.data.recent || [])[0];
                toast.success(`New order from ${fresh?.client_company || fresh?.client_name || "client"}`, {
                    description: `${fresh?.order_number} · £${fresh?.total?.toFixed?.(2) || "?"}`,
                    action: {
                        label: "View",
                        onClick: () => nav("/admin/orders"),
                    },
                });
            }
            prevRef.current = n;
            setCount(n);
        } catch (e) {
            // silent
        }
    };

    useEffect(() => {
        poll();
        const id = setInterval(poll, 20000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = async () => {
        nav("/admin/orders");
        try {
            await api.post("/notifications/mark-orders-seen", {});
            setCount(0);
            prevRef.current = 0;
        } catch (e) {
            // ignore
        }
    };

    const Icon = count > 0 ? BellRing : Bell;

    return (
        <button
            onClick={handleClick}
            className="relative inline-flex h-9 w-9 items-center justify-center border border-black hover:bg-black hover:text-white transition-colors"
            data-testid="admin-notification-bell"
            title={count > 0 ? `${count} new order${count === 1 ? "" : "s"}` : "No new orders"}
        >
            <Icon className={`h-4 w-4 ${count > 0 ? "animate-pulse" : ""}`} strokeWidth={1.5}/>
            {count > 0 && (
                <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-mono flex items-center justify-center"
                    data-testid="admin-notification-count"
                >
                    {count > 99 ? "99+" : count}
                </span>
            )}
        </button>
    );
}
